export function drawMeasureGuides(context, stageContext) {
  const { selection, action, scale } = stageContext.providers;
  // Only active when Alt/Option is pressed
  if (!action.isAltKeyPressed) return;

  const selectedNodes = stageContext.api.getSelectedNodes();
  // Only works when exactly one object (or a consolidated selection group) is selected
  // For simplicity, let's start with single selection support, or bounding box of selection
  if (selectedNodes.length === 0) return;

  // Get the target node to measure against (the one under cursor)
  // We use the first hovered node that is NOT in the selection
  const hoveredId = selection.hovers.find((id) => !selectedNodes.some((n) => n.id === id));
  if (!hoveredId) return;

  const hoveredNode = stageContext.api.getNode(hoveredId);
  if (!hoveredNode) return;

  const selectedRegion = stageContext.api.getSelectedNodesRegion();
  const targetRegion = stageContext.api.getNodeRegion(hoveredNode);

  // Transform to screen space for rendering, or keep in stage space and scale context?
  // Usually render helpers in stage space with inverse scale applied to line width/text size,
  // OR transform rects to screen space.
  // The interactive render cycle seems to mostly work in stage space coordinates,
  // but context is transformed?
  // Let's check renderInteractive in interactive.js:
  // It calls renderInteractiveNodeBorder which usually handles transforms.
  // But wait, context.save() is called but no transform is applied in renderInteractive itself
  // EXCEPT: context is the canvas context.
  // In `renderMain` usually the context is transformed.
  // But `renderInteractive` in `interactive.js` (line 427) uses `renderContext.context`.
  // Let's assume the context is currently in "Screen Space" or "Stage Space"?
  // Re-reading interactive.js:
  // "point = renderContext.providers.scale.offsetTransform(screenPoint)" implies logic uses Stage Space.
  // But drawing?
  // "drawRectRegion(context, box, ...)" uses "box = scale.boxTransform(rect)".
  // So the context is likely in SCREEN space (identity transform), and we manually transform coordinates.

  const selBox = scale.boxTransform(selectedRegion);
  const tgtBox = scale.boxTransform(targetRegion);

  drawMeasurements(context, selBox, tgtBox);
}

function drawMeasurements(context, sel, tgt) {
  context.save();
  context.strokeStyle = '#f50'; // Red-orange standard color
  context.fillStyle = '#f50';
  context.lineWidth = 1;
  context.font = '12px Inter, system-ui, sans-serif';
  context.textBaseline = 'middle';
  context.textAlign = 'center';

  // Determine relationship
  // 1. Tgt contains Sel (Sel is inside Tgt)
  // 2. Sel contains Tgt
  // 3. Disjoint
  // 4. Intersecting

  // We want to measure distance from Sel TO Tgt.

  // Vertical measurements
  if (isHorizontalOverlap(sel, tgt)) {
    // Top to Bottom or Bottom to Top
    if (sel.y > tgt.y + tgt.h) {
      // Sel is below Tgt
      drawVerticalLine(context, sel, tgt.y + tgt.h, sel.y, sel.x + sel.w / 2);
    } else if (sel.y + sel.h < tgt.y) {
      // Sel is above Tgt
      drawVerticalLine(context, sel, sel.y + sel.h, tgt.y, sel.x + sel.w / 2);
    } else {
      // Overlap vertically or contained
      // Draw to top edge
      if (sel.y > tgt.y) {
        drawVerticalLine(context, sel, tgt.y, sel.y, sel.x + sel.w / 2);
      }
      // Draw to bottom edge
      if (sel.y + sel.h < tgt.y + tgt.h) {
        drawVerticalLine(context, sel, sel.y + sel.h, tgt.y + tgt.h, sel.x + sel.w / 2);
      }
    }
  } else {
    // If not overlapping horizontally, maybe we still want to show vertical alignment snap?
    // Standard behavior: if disjoint, show lines to the projection of the target.
    // For MVP, let's stick to direct orthogonal measurements when somewhat aligned or overlapping.
    // Actually, Figma shows vertical gap even if they are not overlapping horizontally,
    // it draws a dashed extension line from the target.
  }

  // Horizontal measurements
  if (isVerticalOverlap(sel, tgt)) {
    if (sel.x > tgt.x + tgt.w) {
      // Sel is right of Tgt
      drawHorizontalLine(context, sel, tgt.x + tgt.w, sel.x, sel.y + sel.h / 2);
    } else if (sel.x + sel.w < tgt.x) {
      // Sel is left of Tgt
      drawHorizontalLine(context, sel, sel.x + sel.w, tgt.x, sel.y + sel.h / 2);
    } else {
      // Overlap or contained
      if (sel.x > tgt.x) {
        drawHorizontalLine(context, sel, tgt.x, sel.x, sel.y + sel.h / 2);
      }
      if (sel.x + sel.w < tgt.x + tgt.w) {
        drawHorizontalLine(context, sel, sel.x + sel.w, tgt.x + tgt.w, sel.y + sel.h / 2);
      }
    }
  }

  // Handle disjoint diagonal case with extension lines?
  // Let's implement the "extension line" logic for robust feel.
  if (!isHorizontalOverlap(sel, tgt)) {
    // Determine vertical gap center
    const centerSelX = sel.x + sel.w / 2;
    const centerTgtX = tgt.x + tgt.w / 2;

    // If we are strictly above/below
    // Actually, if completely diagonal, we show both horizontal and vertical distances
    // projected to the edges.

    // Vertical gap
    let y1, y2;
    if (sel.y > tgt.y + tgt.h) {
      // Sel below
      y1 = tgt.y + tgt.h;
      y2 = sel.y;
    } else if (sel.y + sel.h < tgt.y) {
      // Sel above
      y1 = sel.y + sel.h;
      y2 = tgt.y;
    }

    if (y1 !== undefined) {
      // Draw extension from target to match sel X
      // We draw the vertical measurement line at Sel's center X
      // But we need to draw a horizontal dashed line from Tgt edge to that X
      drawVerticalLine(context, sel, y1, y2, centerSelX);
      drawExtensionLine(context, centerSelX, y1, centerTgtX); // Simplified
    }
  }

  if (!isVerticalOverlap(sel, tgt)) {
    // Horizontal gap
    let x1, x2;
    if (sel.x > tgt.x + tgt.w) {
      // Sel Right
      x1 = tgt.x + tgt.w;
      x2 = sel.x;
    } else if (sel.x + sel.w < tgt.x) {
      // Sel Left
      x1 = sel.x + sel.w;
      x2 = tgt.x;
    }

    if (x1 !== undefined) {
      const centerSelY = sel.y + sel.h / 2;
      const centerTgtY = tgt.y + tgt.h / 2;
      drawHorizontalLine(context, sel, x1, x2, centerSelY);
      drawExtensionLine(context, x1, centerSelY, x1, centerTgtY); // vertical extension
    }
  }

  context.restore();
}

function isHorizontalOverlap(r1, r2) {
  return r1.x < r2.x + r2.w && r1.x + r1.w > r2.x;
}

function isVerticalOverlap(r1, r2) {
  return r1.y < r2.y + r2.h && r1.y + r1.h > r2.y;
}

function drawVerticalLine(context, box, y1, y2, x) {
  const dist = Math.abs(y2 - y1);
  if (dist < 1) return;

  context.beginPath();
  context.moveTo(x, y1);
  context.lineTo(x, y2);
  context.stroke();

  // Ends
  const capSize = 4;
  context.beginPath();
  context.moveTo(x - capSize, y1);
  context.lineTo(x + capSize, y1);
  context.moveTo(x - capSize, y2);
  context.lineTo(x + capSize, y2);
  context.stroke();

  // Label
  drawLabel(context, Math.round(dist), x, (y1 + y2) / 2);
}

function drawHorizontalLine(context, box, x1, x2, y) {
  const dist = Math.abs(x2 - x1);
  if (dist < 1) return;

  context.beginPath();
  context.moveTo(x1, y);
  context.lineTo(x2, y);
  context.stroke();

  // Ends
  const capSize = 4;
  context.beginPath();
  context.moveTo(x1, y - capSize);
  context.lineTo(x1, y + capSize);
  context.moveTo(x2, y - capSize);
  context.lineTo(x2, y + capSize);
  context.stroke();

  // Label
  drawLabel(context, Math.round(dist), (x1 + x2) / 2, y);
}

function drawExtensionLine(context, x1, y1, x2, y2) {
  if (x2 === undefined) x2 = x1;
  if (y2 === undefined) y2 = y1;

  context.save();
  context.setLineDash([2, 2]);
  context.strokeStyle = '#f50';
  context.globalAlpha = 0.5;
  context.beginPath();
  context.moveTo(x1, y1);
  context.lineTo(x2, y2);
  context.stroke();
  context.restore();
}

function drawLabel(context, text, x, y) {
  const padding = 4;
  const width = context.measureText(text).width + padding * 2;
  const height = 16;

  context.save();
  context.fillStyle = '#f50';
  context.beginPath();
  context.roundRect(x - width / 2, y - height / 2, width, height, 4);
  context.fill();

  context.fillStyle = '#fff';
  context.fillText(text, x, y);
  context.restore();
}
